import React, { useState, useRef, useCallback } from 'react';
import './ComplianceUpload.css';

const API_BASE_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';
const API_KEY = ''; // Step 41 — auth via httpOnly cookie; this stub keeps existing 'Bearer ' headers harmless

const DOC_TYPE_LABELS = {
  epc: 'EPC (Energy Performance Certificate)',
  gas_safety: 'Gas Safety Certificate',
  eicr: 'EICR (Electrical Installation Condition Report)',
};

const CONFIDENCE_CONFIG = {
  high: { className: 'confidence-high', label: 'High confidence', icon: '\u2713' },
  medium: { className: 'confidence-medium', label: 'Please verify', icon: '!' },
  low: { className: 'confidence-low', label: 'Please verify this field', icon: '!' },
};

function ConfidenceIndicator({ level }) {
  const config = CONFIDENCE_CONFIG[level] || CONFIDENCE_CONFIG.low;
  return (
    <span className={`confidence-badge ${config.className}`} data-testid={`confidence-${level}`}>
      <span className="confidence-icon">{config.icon}</span>
      {config.label}
    </span>
  );
}

export default function ComplianceUpload({ onClose, onSaved, initialDocType, initialPropertyId }) {
  const [step, setStep] = useState('upload'); // upload | extracting | review | saving | done
  const [file, setFile] = useState(null);
  const [hintDocType, setHintDocType] = useState(initialDocType || '');
  const [hintPropertyId, setHintPropertyId] = useState(initialPropertyId || '');
  const [error, setError] = useState(null);
  const [extractionResult, setExtractionResult] = useState(null);
  const [editedFields, setEditedFields] = useState({});
  const [confirmedFields, setConfirmedFields] = useState({});
  const [saveError, setSaveError] = useState(null);
  const [reExtracting, setReExtracting] = useState(false);
  const fileInputRef = useRef(null);

  const headers = {
    'Authorization': `Bearer ${API_KEY}`,
  };

  const handleFileSelect = (e) => {
    const selected = e.target.files[0];
    if (!selected) return;
    const allowed = ['application/pdf', 'image/jpeg', 'image/png', 'image/jpg'];
    if (!allowed.includes(selected.type)) {
      setError('Only PDF, JPG, and PNG files are accepted.');
      return;
    }
    if (selected.size > 20 * 1024 * 1024) {
      setError('File must be under 20MB.');
      return;
    }
    setError(null);
    setFile(selected);
  };

  const handleUpload = async () => {
    if (!file) return;
    setStep('extracting');
    setError(null);

    const formData = new FormData();
    formData.append('file', file);
    if (hintDocType) formData.append('hint_doc_type', hintDocType);
    if (hintPropertyId) formData.append('hint_property_id', hintPropertyId);

    try {
      const res = await fetch(`${API_BASE_URL}/api/compliance/upload-and-extract`, {
        method: 'POST',
        headers,
        body: formData,
      });
      const resText = await res.text();
      const resData = resText ? JSON.parse(resText) : {};
      if (!res.ok) {
        throw new Error(resData.detail || 'Upload failed');
      }
      setExtractionResult(resData);

      // Initialise edited fields with extracted values
      const initial = {};
      const fields = resData.fields || {};
      Object.keys(fields).forEach(key => {
        initial[key] = fields[key].value;
      });
      setEditedFields(initial);

      // Auto-confirm high-confidence fields, require confirmation for medium/low/null
      const autoConfirmed = {};
      Object.keys(fields).forEach(key => {
        const f = fields[key];
        if (f.confidence === 'high' && f.value != null && f.value !== '') {
          autoConfirmed[key] = true;
        }
      });
      setConfirmedFields(autoConfirmed);

      setStep('review');
    } catch (err) {
      setError(err.message);
      setStep('upload');
    }
  };

  const handleFieldChange = (fieldKey, value) => {
    setEditedFields(prev => ({ ...prev, [fieldKey]: value }));
    // When user edits a field, mark it as confirmed
    setConfirmedFields(prev => ({ ...prev, [fieldKey]: true }));
  };

  const handleConfirmField = (fieldKey) => {
    setConfirmedFields(prev => ({ ...prev, [fieldKey]: true }));
  };

  // Check if all required fields are confirmed
  const allFieldsConfirmed = useCallback(() => {
    if (!extractionResult?.fields) return false;
    const fields = extractionResult.fields;
    // Every field must be confirmed, even empty ones (agent acknowledges it's empty)
    return Object.keys(fields).every(key => confirmedFields[key] === true);
  }, [extractionResult, confirmedFields]);

  const handleSave = async () => {
    if (!allFieldsConfirmed()) return;
    setStep('saving');
    setSaveError(null);

    try {
      const res = await fetch(`${API_BASE_URL}/api/compliance/confirm-and-save`, {
        method: 'POST',
        headers: { ...headers, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          upload_id: extractionResult.upload_id,
          property_id: editedFields.property_id,
          doc_type: editedFields.doc_type,
          expiry_date: editedFields.expiry_date,
          issue_date: editedFields.issue_date,
          issuing_company: editedFields.issuing_company,
          extraction_fields: extractionResult.fields,
        }),
      });
      if (!res.ok) {
        const errData = await res.json().catch(() => ({}));
        throw new Error(errData.detail || 'Save failed');
      }
      setStep('done');
      if (onSaved) onSaved();
    } catch (err) {
      setSaveError(err.message);
      setStep('review');
    }
  };

  const handleReExtract = async () => {
    if (!extractionResult?.upload_id) return;
    setReExtracting(true);
    setSaveError(null);

    try {
      const res = await fetch(`${API_BASE_URL}/api/compliance/re-extract`, {
        method: 'POST',
        headers: { ...headers, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          upload_id: extractionResult.upload_id,
          hint_doc_type: editedFields.doc_type || null,
          hint_property_id: editedFields.property_id || null,
        }),
      });
      const resText = await res.text();
      const resData = resText ? JSON.parse(resText) : {};
      if (!res.ok) {
        throw new Error(resData.detail || 'Re-extraction failed');
      }
      setExtractionResult(resData);

      const initial = {};
      const fields = resData.fields || {};
      Object.keys(fields).forEach(key => {
        initial[key] = fields[key].value;
      });
      setEditedFields(initial);

      const autoConfirmed = {};
      Object.keys(fields).forEach(key => {
        const f = fields[key];
        if (f.confidence === 'high' && f.value != null && f.value !== '') {
          autoConfirmed[key] = true;
        }
      });
      setConfirmedFields(autoConfirmed);
    } catch (err) {
      setSaveError(err.message);
    } finally {
      setReExtracting(false);
    }
  };

  const needsVerification = (fieldKey) => {
    if (!extractionResult?.fields?.[fieldKey]) return true;
    const f = extractionResult.fields[fieldKey];
    return f.confidence !== 'high' || f.value == null || f.value === '';
  };

  const getFieldConfidence = (fieldKey) => {
    if (!extractionResult?.fields?.[fieldKey]) return 'low';
    const f = extractionResult.fields[fieldKey];
    if (f.value == null || f.value === '') return null; // unextracted
    return f.confidence;
  };

  const renderUploadStep = () => (
    <div className="upload-step" data-testid="compliance-upload-step">
      <div className="upload-zone" onClick={() => fileInputRef.current?.click()} data-testid="upload-zone">
        <div className="upload-icon">
          <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
            <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
            <polyline points="17 8 12 3 7 8" />
            <line x1="12" y1="3" x2="12" y2="15" />
          </svg>
        </div>
        <p className="upload-label">Click to select a compliance document</p>
        <p className="upload-hint">PDF, JPG, or PNG — max 20MB</p>
        <input
          ref={fileInputRef}
          type="file"
          accept=".pdf,.jpg,.jpeg,.png"
          onChange={handleFileSelect}
          style={{ display: 'none' }}
          data-testid="file-input"
        />
      </div>

      {file && (
        <div className="selected-file" data-testid="selected-file">
          <span className="file-name">{file.name}</span>
          <span className="file-size">({(file.size / 1024).toFixed(0)} KB)</span>
        </div>
      )}

      <div className="upload-hints-row">
        <div className="hint-field">
          <label>Document type (optional hint)</label>
          <select
            value={hintDocType}
            onChange={(e) => setHintDocType(e.target.value)}
            data-testid="hint-doc-type"
          >
            <option value="">AI will detect</option>
            {Object.entries(DOC_TYPE_LABELS).map(([k, v]) => (
              <option key={k} value={k}>{v}</option>
            ))}
          </select>
        </div>
      </div>

      {error && <div className="upload-error" data-testid="upload-error">{error}</div>}

      <button
        className="btn-primary btn-upload"
        disabled={!file}
        onClick={handleUpload}
        data-testid="upload-extract-btn"
      >
        Upload &amp; Extract with AI
      </button>
    </div>
  );

  const renderExtracting = () => (
    <div className="extracting-step" data-testid="extracting-step">
      <div className="extracting-spinner"></div>
      <p className="extracting-label">Analysing document with AI...</p>
      <p className="extracting-hint">Claude is reading {file?.name}</p>
    </div>
  );

  const renderReviewStep = () => {
    if (!extractionResult) return null;
    const fields = extractionResult.fields;
    const propertyOptions = extractionResult.property_options || [];
    const canSave = allFieldsConfirmed() && editedFields.property_id && editedFields.doc_type;

    return (
      <div className="review-step" data-testid="compliance-review-step">
        <div className="review-header">
          <h3>AI Extraction Results</h3>
          <p className="review-subtitle">
            Review each field below. Verify or correct flagged fields before saving.
          </p>
          {extractionResult.extraction_error && (
            <div className="extraction-warning" data-testid="extraction-warning">
              AI extraction encountered an issue: {extractionResult.extraction_error}.
              Please fill all fields manually.
            </div>
          )}
          {extractionResult.tokens_used && (
            <p className="tokens-info" data-testid="tokens-used">
              Tokens used: {extractionResult.tokens_used.input} input / {extractionResult.tokens_used.output} output
            </p>
          )}
          <button
            className="btn-reextract"
            onClick={handleReExtract}
            disabled={reExtracting}
            data-testid="re-extract-btn"
          >
            {reExtracting ? 'Re-analysing...' : 'Re-run AI Extraction'}
          </button>
        </div>

        <div className="review-fields">
          {/* Document Type */}
          <ReviewField
            label="Document Type"
            fieldKey="doc_type"
            confidence={getFieldConfidence('doc_type')}
            confirmed={confirmedFields.doc_type}
            needsVerify={needsVerification('doc_type')}
            onConfirm={() => handleConfirmField('doc_type')}
          >
            <select
              value={editedFields.doc_type || ''}
              onChange={(e) => handleFieldChange('doc_type', e.target.value)}
              data-testid="field-doc-type"
              className={!editedFields.doc_type ? 'field-empty' : ''}
            >
              <option value="">-- Select --</option>
              {Object.entries(DOC_TYPE_LABELS).map(([k, v]) => (
                <option key={k} value={k}>{v}</option>
              ))}
            </select>
          </ReviewField>

          {/* Property */}
          <ReviewField
            label="Property"
            fieldKey="property_id"
            confidence={getFieldConfidence('property_id')}
            confirmed={confirmedFields.property_id}
            needsVerify={needsVerification('property_id')}
            onConfirm={() => handleConfirmField('property_id')}
          >
            <select
              value={editedFields.property_id || ''}
              onChange={(e) => handleFieldChange('property_id', e.target.value)}
              data-testid="field-property-id"
              className={!editedFields.property_id ? 'field-empty' : ''}
            >
              <option value="">-- Select Property --</option>
              {propertyOptions.map(p => (
                <option key={p.id} value={p.id}>{p.address}</option>
              ))}
            </select>
          </ReviewField>

          {/* Expiry Date */}
          <ReviewField
            label="Expiry Date"
            fieldKey="expiry_date"
            confidence={getFieldConfidence('expiry_date')}
            confirmed={confirmedFields.expiry_date}
            needsVerify={needsVerification('expiry_date')}
            onConfirm={() => handleConfirmField('expiry_date')}
            displayNote={fields?.expiry_date?.display_value ? `AI read: ${fields.expiry_date.display_value}` : null}
          >
            <input
              type="date"
              value={editedFields.expiry_date || ''}
              onChange={(e) => handleFieldChange('expiry_date', e.target.value)}
              data-testid="field-expiry-date"
              className={!editedFields.expiry_date ? 'field-empty' : ''}
            />
          </ReviewField>

          {/* Issue Date */}
          <ReviewField
            label="Issue Date"
            fieldKey="issue_date"
            confidence={getFieldConfidence('issue_date')}
            confirmed={confirmedFields.issue_date}
            needsVerify={needsVerification('issue_date')}
            onConfirm={() => handleConfirmField('issue_date')}
            displayNote={fields?.issue_date?.display_value ? `AI read: ${fields.issue_date.display_value}` : null}
          >
            <input
              type="date"
              value={editedFields.issue_date || ''}
              onChange={(e) => handleFieldChange('issue_date', e.target.value)}
              data-testid="field-issue-date"
              className={!editedFields.issue_date ? 'field-empty' : ''}
            />
          </ReviewField>

          {/* Issuing Company */}
          <ReviewField
            label="Issuing Company / Assessor"
            fieldKey="issuing_company"
            confidence={getFieldConfidence('issuing_company')}
            confirmed={confirmedFields.issuing_company}
            needsVerify={needsVerification('issuing_company')}
            onConfirm={() => handleConfirmField('issuing_company')}
          >
            <input
              type="text"
              value={editedFields.issuing_company || ''}
              onChange={(e) => handleFieldChange('issuing_company', e.target.value)}
              placeholder="Enter company name"
              data-testid="field-issuing-company"
              className={!editedFields.issuing_company ? 'field-empty' : ''}
            />
          </ReviewField>

        </div>

        {saveError && <div className="upload-error" data-testid="save-error">{saveError}</div>}

        <div className="review-actions">
          <button className="btn-secondary" onClick={() => { setStep('upload'); setFile(null); setExtractionResult(null); }} data-testid="back-to-upload-btn">
            Upload Different File
          </button>
          <button
            className="btn-primary btn-save"
            disabled={!canSave}
            onClick={handleSave}
            data-testid="save-document-btn"
          >
            {canSave ? 'Save Document' : 'Confirm all fields to save'}
          </button>
        </div>
      </div>
    );
  };

  const renderDone = () => (
    <div className="done-step" data-testid="compliance-done-step">
      <div className="done-icon">
        <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="#10b981" strokeWidth="2">
          <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
          <polyline points="22 4 12 14.01 9 11.01" />
        </svg>
      </div>
      <h3>Document Saved Successfully</h3>
      <p>The compliance document has been saved to the property record.</p>
      <div className="done-actions">
        <button className="btn-primary" style={{ background: '#0073EA', color: '#fff' }} onClick={() => { setStep('upload'); setFile(null); setExtractionResult(null); }} data-testid="upload-another-btn">
          Upload Another
        </button>
        <button className="btn-secondary" onClick={onClose} data-testid="close-upload-btn">
          Close
        </button>
      </div>
    </div>
  );

  return (
    <div className="compliance-upload-panel" data-testid="compliance-upload-panel">
      <div className="upload-panel-header">
        <h2>Upload Compliance Document</h2>
        <button className="btn-close-panel" onClick={onClose} data-testid="close-panel-btn">&times;</button>
      </div>
      <div className="upload-panel-body">
        {step === 'upload' && renderUploadStep()}
        {step === 'extracting' && renderExtracting()}
        {step === 'review' && renderReviewStep()}
        {step === 'saving' && renderExtracting()}
        {step === 'done' && renderDone()}
      </div>
    </div>
  );
}

function ReviewField({ label, fieldKey, confidence, confirmed, needsVerify, onConfirm, children, displayNote }) {
  const isUnextracted = confidence === null;
  const fieldClass = isUnextracted
    ? 'review-field field-unextracted'
    : needsVerify && !confirmed
      ? 'review-field field-needs-verify'
      : confirmed
        ? 'review-field field-confirmed'
        : 'review-field';

  return (
    <div className={fieldClass} data-testid={`review-field-${fieldKey}`}>
      <div className="field-header">
        <label className="field-label">{label}</label>
        {isUnextracted ? (
          <span className="confidence-badge confidence-unextracted" data-testid={`confidence-unextracted-${fieldKey}`}>
            <span className="confidence-icon">?</span>
            Not extracted — enter manually
          </span>
        ) : (
          <ConfidenceIndicator level={confidence} />
        )}
      </div>
      <div className="field-input">
        {children}
      </div>
      {displayNote && <p className="field-note">{displayNote}</p>}
      {needsVerify && !confirmed && (
        <button
          className="btn-confirm-field"
          onClick={onConfirm}
          data-testid={`confirm-field-${fieldKey}`}
        >
          Confirm this field
        </button>
      )}
      {confirmed && (
        <span className="field-confirmed-label" data-testid={`confirmed-label-${fieldKey}`}>Confirmed</span>
      )}
    </div>
  );
}
